import { useEffect, useState } from "react";
import { useFetcher } from "react-router-dom";
import { CountsData } from "../book-content/searchCounts.ts";
import { SearchCountResponse } from "../../../../common/api/decimal/methods/search.ts";
import useSearchFavoritesSync from "./useSearchFavoritesSync.ts";
import { SearchStatus } from "../types.ts";

export default function useContentResults(query: string) {
  const fetcher = useFetcher<CountsData>();
  const [allResults, setAllResults] = useState<SearchCountResponse[]>();
  const [recentResults, setRecentResults] = useState<SearchCountResponse[]>();
  const [favoritedContentVbids, setFavoritedContentVbids] =
    useState<string[]>();

  // search does not revalidate so need to handle favorites in state

  useSearchFavoritesSync<SearchCountResponse>(allResults, (updatedResults) => {
    setAllResults(updatedResults);
  });

  useSearchFavoritesSync<SearchCountResponse>(
    recentResults,
    (updatedResults) => {
      setRecentResults(updatedResults);
    }
  );

  useEffect(() => {
    if (fetcher.state === "idle" && fetcher.data) {
      if (!allResults) {
        setAllResults(fetcher.data?.results?.allResults ?? []);
      }
      if (!recentResults) {
        setRecentResults(fetcher.data?.results?.recentResults ?? []);
      }

      // capture favorites at start so content results doesn't move around
      // when live favorites change
      if (fetcher.data?.results && !favoritedContentVbids) {
        const faves =
          fetcher.data?.results?.allResults
            ?.filter((hit) => hit.asset.favorite.isFavorited)
            .map((hit) => hit.vbid) ?? [];
        setFavoritedContentVbids(faves);
      }
    }
  }, [
    allResults,
    favoritedContentVbids,
    fetcher.data,
    fetcher.state,
    recentResults,
  ]);

  useEffect(() => {
    if (query && fetcher.state === "idle" && !fetcher.data) {
      fetcher.load(`/search/api/counts?${query}`);
    }
  }, [fetcher, query]);

  const allResultsVal = allResults ?? [];
  const recentResultsVal = recentResults ?? [];

  const status: SearchStatus =
    !allResults || !recentResults
      ? "loading"
      : !allResults.length && !recentResults.length
      ? "empty"
      : "has-results";

  return {
    status,
    allResults: allResultsVal,
    recentResults: recentResultsVal,
    favoritedContentVbids,
    nextCursor: fetcher.data?.results?.nextCursor,
  };
}
